import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogOut, User, Calendar, FileText, Receipt, Activity, UserPlus } from 'lucide-react';
import { useRoleStore } from '@/core/application/state/useRoleStore';

interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  path: string;
  isActive?: boolean;
}

interface ProfessionalLayoutProps {
  children: React.ReactNode;
}

export function ProfessionalLayout({ children }: Readonly<ProfessionalLayoutProps>) {
  const theme = useRoleStore((s) => s.getTheme());
  const setActiveRole = useRoleStore((s) => s.setActiveRole);
  
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;

  // Restaurar el rol si el usuario presiona F5 (Web Reload)
  useEffect(() => {
    setActiveRole('PROFESSIONAL');
  }, [setActiveRole]);

  const navItems: NavItem[] = [
    {
      id: 'Hub',
      label: 'Hub',
      icon: LogOut,
      path: '/hub',
    },
    {
      id: 'ProfileEditor',
      label: 'Mi Perfil',
      icon: User,
      path: '/pro/edit',
      isActive: currentPath.includes('/pro/edit'),
    },
    {
      id: 'AgendaConfig',
      label: 'Agenda',
      icon: Calendar,
      path: '/pro/AgendaConfig',
      isActive: currentPath.includes('/pro/AgendaConfig'),
    },
    {
      id: 'MiConsultorioFaq',
      label: 'Servicios',
      icon: FileText,
      path: '/pro/MiConsultorioFaq',
      isActive: currentPath.includes('/pro/MiConsultorioFaq'),
    },
    {
      id: 'ComprobantesQueue',
      label: 'Pagos',
      icon: Receipt,
      path: '/pro/comprobantes',
      isActive: currentPath.includes('/pro/comprobantes'),
    },
    {
      id: 'MiEquipo',
      label: 'Mi Equipo',
      icon: UserPlus,
      path: '/pro/mi-equipo',
      isActive: currentPath.includes('/pro/mi-equipo'),
    }
  ];

  return (
    <div 
      className="flex h-dvh flex-col md:flex-row w-full overflow-hidden" 
      style={{ backgroundColor: theme.background }}
    >
      
      {/* ============================================
          SIDEBAR (ESCRITORIO / TABLET)
          ============================================ */}
      <aside 
        className="hidden md:flex flex-col w-[280px] border-r border-[#E2E8F0] pt-8 shrink-0"
        style={{ backgroundColor: theme.surface }}
      >
        <div className="flex flex-col items-center mb-10">
          <Activity size={40} color={theme.primary} />
          <h2 className="text-xl font-extrabold mt-3" style={{ color: theme.text }}>
            Mi Consultorio
          </h2>
        </div>
        
        <nav className="flex-1 flex flex-col w-full">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => navigate(item.path)}
                className="flex items-center w-full px-8 py-4 mb-2 cursor-pointer transition-colors duration-200 border-r-4"
                style={{
                  backgroundColor: item.isActive ? `${theme.primary}1A` : 'transparent',
                  borderColor: item.isActive ? theme.primary : 'transparent',
                }}
              >
                <Icon size={28} color={item.isActive ? theme.primary : theme.textSecondary || '#64748B'} />
                <span 
                  className={`text-lg ml-4 ${item.isActive ? 'font-bold' : 'font-normal'}`}
                  style={{ color: item.isActive ? theme.primary : theme.textSecondary || '#64748B' }}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
        </nav>
      </aside>
      
      {/* ============================================
          CONTENIDO PRINCIPAL
          ============================================ */}
      <main className="flex-1 flex flex-col overflow-y-auto">
        <div className="w-full max-w-7xl mx-auto flex-1 p-6 gap-6 md:p-8 md:gap-8 flex flex-col">
          {children}
        </div>
      </main>

      {/* ============================================
          BOTTOM TAB BAR (MÓVIL)
          ============================================ */}
      <nav 
        className="md:hidden flex flex-row border-t border-[#E2E8F0] h-[76px] shrink-0 shadow-[0_-4px_12px_rgba(0,0,0,0.05)]"
        style={{ backgroundColor: theme.surface }}
      >
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              className="flex-1 flex flex-col justify-center items-center bg-transparent border-none py-2 cursor-pointer"
            >
              <Icon size={28} color={item.isActive ? theme.primary : theme.textSecondary || '#64748B'} />
              <span 
                className={`text-xs mt-1 ${item.isActive ? 'font-bold' : 'font-normal'}`}
                style={{ color: item.isActive ? theme.primary : theme.textSecondary || '#64748B' }}
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </nav>

    </div>
  );
}
